import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { InvitePreview } from "@/domain/model/Invitation";
import type { PreviewInvitation } from "@/domain/usecase/PreviewInvitation";
import type { AcceptInvitation } from "@/domain/usecase/AcceptInvitation";
import {
  buildAcceptInvitationSchema,
  initialAcceptInvitationFormValues,
  type AcceptInvitationFormValues,
  type AcceptInvitationLegalVersions } from "./acceptInvitationSchema";

type FieldErrors = Record<string, string>;

type PreviewState =
  | { type: "missing-token" }
  | { type: "loading" }
  | { type: "ready"; preview: InvitePreview }
  | { type: "failed"; message: string };

type SubmitState =
  | { type: "idle" }
  | { type: "submitting" }
  | {
    type: "accepted-dashboard";
    email: string;
    dashboardPath: "/dashboard";
  }
  | {
    type: "accepted-android";
    email: string;
  }
  | { type: "failed"; message: string };

type Args = {
  token: string;
  legalVersions: AcceptInvitationLegalVersions;
  previewInvitation: PreviewInvitation;
  acceptInvitation: AcceptInvitation;
};

function toUserMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
}

function toFieldErrors(error: unknown): FieldErrors {
  const result: FieldErrors = {};

  if (
    typeof error === "object" &&
    error !== null &&
    "issues" in error &&
    Array.isArray(error.issues)
  ) {
    for (const issue of error.issues) {
      const path = Array.isArray(issue.path) ? issue.path.join(".") : "";
      if (path && typeof issue.message === "string") {
        result[path] = issue.message;
      }
    }
  }

  return result;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function useAcceptInvitationViewModel({
  token,
  legalVersions,
  previewInvitation,
  acceptInvitation,
}: Args) {
  const [previewState, setPreviewState] = useState<PreviewState>(() =>
    token ? { type: "loading" } : { type: "missing-token" },
  );

  const [submitState, setSubmitState] = useState<SubmitState>({
    type: "idle",
  });

  const [values, setValues] = useState<AcceptInvitationFormValues>(
    initialAcceptInvitationFormValues,
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const submitAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      submitAbortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setPreviewState({ type: "missing-token" });
      return;
    }

    const controller = new AbortController();

    async function loadPreview() {
      setPreviewState({ type: "loading" });

      try {
        const preview = await previewInvitation.execute(
          token,
          controller.signal,
        );

        setPreviewState({ type: "ready", preview });
      } catch (error) {
        if (isAbortError(error)) return;

        setPreviewState({
          type: "failed",
          message: toUserMessage(
            error,
            "The invitation could not be loaded. It may be invalid or expired.",
          ),
        });
      }
    }

    void loadPreview();

    return () => {
      controller.abort();
    };
  }, [token, previewInvitation]);

  const preview =
    previewState.type === "ready" ? previewState.preview : undefined;

  const isBusy =
    previewState.type === "loading" || submitState.type === "submitting";

  const canSubmit =
    previewState.type === "ready" && submitState.type !== "submitting";

  const setField = useCallback(
    <K extends keyof AcceptInvitationFormValues>(
      field: K,
      value: AcceptInvitationFormValues[K],
    ) => {
      setValues((current) => ({
        ...current,
        [field]: value,
      }));

      setFieldErrors((current) => {
        const next = { ...current };
        delete next[String(field)];
        return next;
      });
    },
    [],
  );

  const setPersonField = useCallback(
    <K extends keyof AcceptInvitationFormValues["person"]>(
      field: K,
      value: AcceptInvitationFormValues["person"][K],
    ) => {
      setValues((current) => ({
        ...current,
        person: {
          ...current.person,
          [field]: value,
        },
      }));

      setFieldErrors((current) => {
        const next = { ...current };
        delete next[`person.${String(field)}`];
        return next;
      });
    },
    [],
  );

  const submit = useCallback(async () => {
    if (previewState.type !== "ready") return;

    setSubmitState({ type: "idle" });
    setFieldErrors({});

    const schema = buildAcceptInvitationSchema(
      previewState.preview.requiresPerson,
      legalVersions
    );

    const parsed = schema.safeParse(values);

    if (!parsed.success) {
      setFieldErrors(toFieldErrors(parsed.error));
      return;
    }

    submitAbortControllerRef.current?.abort();

    const controller = new AbortController();
    submitAbortControllerRef.current = controller;

    setSubmitState({ type: "submitting" });

    try {
      const outcome = await acceptInvitation.execute(
        {
          token,
          password: parsed.data.password,
          person: parsed.data.person,
          acceptedUserTermsVersion: parsed.data.acceptedUserTermsVersion,
          acceptedPrivacyVersion: parsed.data.acceptedPrivacyVersion,
        },
        controller.signal,
      );

      if (controller.signal.aborted) return;

      if (outcome.type === "web-dashboard") {
        setSubmitState({
          type: "accepted-dashboard",
          email: previewState.preview.email,
          dashboardPath: outcome.dashboardPath,
        });
        return;
      }

      setSubmitState({
        type: "accepted-android",
        email: previewState.preview.email,
      });
    } catch (error) {
      if (isAbortError(error)) return;

      setSubmitState({
        type: "failed",
        message: toUserMessage(
          error,
          "Die Einladung konnte nicht angenommen werden. Bitte versuchen Sie es erneut.",
        ),
      });
    } finally {
      if (submitAbortControllerRef.current === controller) {
        submitAbortControllerRef.current = null;
      }
    }
  }, [acceptInvitation, legalVersions, previewState, token, values]);

  return useMemo(
    () => ({
      previewState,
      submitState,
      preview,
      values,
      fieldErrors,
      isBusy,
      canSubmit,
      actions: {
        setField,
        setPersonField,
        submit,
      },
    }),
    [
      previewState,
      submitState,
      preview,
      values,
      fieldErrors,
      isBusy,
      canSubmit,
      setField,
      setPersonField,
      submit,
    ],
  );
}
